import React from 'react'
import { useApp } from './context/AppContext'
import { Icon, useT, FAKE } from './shared'
import BrandLogo, { useOrgName } from './shared/BrandLogo'
import { Btn } from './shared/components'
import { listProgramVideos, uploadVideoForLesson, deleteVideo } from './api/videos.api'
import { listCourses, getCourse } from './api/courses.api'
import { unreadNotifCount } from './api/notifications.api'
import { getMyEnrollment } from './api/programs.api'
import NotificationsPanel from './screens/NotificationsPanel'
import { GlobalSearch } from './screens/GlobalSearch'
import Sidebar from './components/sidebar/Sidebar'
import ThemeToggle from './components/ThemeToggle'
import { getNav } from './components/sidebar/nav'

// --- Screen imports ---
import LoginScreen from './screens/LoginScreen'
import MfaSetupScreen from './screens/MfaSetupScreen'
import ProgramSelectorScreen from './screens/ProgramSelectorScreen'
import { ParticipantWelcomeScreen, AdminWelcomeScreen } from './screens/WelcomeScreen'
import { ParticipantDashboard, TrainerDashboard, AdminDashboard } from './screens/DashboardScreens'
import { CourseDetailScreen, VideoPlayerScreen } from './screens/LearningScreens'
import { LessonGameScreen } from './screens/LessonGameScreen'
import CourseManageScreen from './screens/CourseManageScreen'
import AIChatScreen, { AIFloatingPanel } from './screens/AIScreen'
import DashboardTutorial from './screens/DashboardTutorial'
import KnowledgeBaseScreen from './screens/KBScreen'
import { TestScreen, LearningGainScreen } from './screens/TestScreens'
import TestEditorScreen from './screens/TestEditorScreen'
import ProgramDetailScreen from './screens/ProgramDetailScreen'
import ReportsScreen from './screens/ReportsScreen'
import { ClaimScreen, AdminCertSendScreen, VerifyPublicScreen, PublicCollectionScreen } from './screens/ClaimScreens'
import { CertificateScreen, ProgressAnalyticsScreen } from './screens/CertAnalyticsScreens'
import SessionDetailScreen from './screens/SessionDetailScreen'
import EmailsScreen from './screens/EmailsScreen'
import QRAttendanceScreen from './screens/QRAttendanceScreen'
import UsersScreen from './screens/UsersScreen'
import SettingsScreen from './screens/SettingsScreen'
import ZoomScreen from './screens/ZoomScreen'
import CMSScreen from './screens/CMSScreen'
import PlatformScreen from './screens/PlatformScreen'
import TrainingScreen from './screens/TrainingScreen'
import ProgramManageScreen from './screens/ProgramManageScreen'
import FeedbackScreen from './screens/FeedbackScreen'
import EnrollmentScreen from './screens/EnrollmentScreen'
import AppraisalScreen from './screens/AppraisalScreens'
import AppraisalReviewScreen from './screens/AppraisalReviewScreen'

// Sidebar dan definisi menunya ada di components/sidebar/. `getNav` masih
// diekspor dari sini karena dipakai ApplicationsPanel dan Topbar.
export { getNav }

// Desktop Topbar — shown inside .main-area above the content
function Topbar({ notifCount, onBellClick }) {
  const { lang, setLang, page, role, navigate, user } = useApp()
  const orgName = useOrgName()
  const t = useT()
  const nav = getNav(role, t, lang, { superAdmin: user?.role === 'SUPER_ADMIN' })

  // Halaman tanpa butir sidebar sendiri memakai label halaman induknya, supaya
  // breadcrumb tidak menampilkan id mentah seperti "session-detail".
  const INDUK_HALAMAN = { "session-detail": "training" }
  const currentItem =
    nav.find((item) => item.id === page) ||
    nav.find((item) => item.id === INDUK_HALAMAN[page])
  const pageLabel = currentItem?.label || page

  return (
    <header className="topbar">
      <div className="topbar-breadcrumb">
        <span>{orgName}</span>
        <span className="sep">/</span>
        <span className="current">{pageLabel}</span>
      </div>
      <div className="topbar-spacer" />
      <div className="topbar-actions">
        {/* FR-21: pencarian + palet menu untuk semua peran; hasil server
            (program, kursus, sesi, pengguna) hanya muncul untuk admin. */}
        <GlobalSearch />
        <ThemeToggle className="icon-btn" />
        <button
          className="icon-btn lang-toggle"
          onClick={() => setLang(lang === 'id' ? 'en' : 'id')}
          title="Switch language"
          style={{ width: 'auto', padding: '0 8px', fontSize: 11, fontFamily: 'var(--font-mono)' }}
        >
          <span className={lang === 'id' ? 'on' : 'off'}>ID</span>
          <span style={{ color: 'var(--text-4)', fontWeight: 400 }}>/</span>
          <span className={lang === 'en' ? 'on' : 'off'}>EN</span>
        </button>
        <button
          className="icon-btn"
          title={t.notifications}
          onClick={onBellClick}
          style={{ position: 'relative' }}
        >
          <Icon name="bell" size={16} />
          {notifCount > 0 && (
            <span className="notif-badge">{notifCount > 99 ? '99+' : notifCount}</span>
          )}
        </button>
        {/* Membuka kembali layar sambutan. Tombol ini dulu tidak melakukan
            apa pun, sementara layar sambutan hanya bisa dilihat sekali seumur
            akun — padahal isinya (ringkasan tindakan yang menunggu, pintasan
            ke aksi yang sering dipakai) justru berguna berulang kali. */}
        <button
          className="icon-btn"
          title={lang === 'id' ? 'Panduan & tindakan cepat' : 'Guide & quick actions'}
          aria-label={lang === 'id' ? 'Panduan & tindakan cepat' : 'Guide & quick actions'}
          onClick={() => navigate('welcome')}
        >
          <Icon name="help" size={16} />
        </button>
      </div>
    </header>
  )
}

// Mobile header — hidden on desktop, appears at top on mobile
function TopNav({ notifCount, onBellClick }) {
  const { lang, setLang, user, navigate, logout, drawerOpen, setDrawerOpen, branding } = useApp()
  const t = useT()

  // FR-28: show the institution's own identity, falling back to the built-in
  // label while branding is still loading.
  const orgName = branding?.institution_name || t.appName

  const initials = user?.fullName
    ? user.fullName.split(' ').map((w) => w[0]).slice(0, 2).join('').toUpperCase()
    : '??'

  return (
    <header className="topnav">
      <button
        className="topnav-btn topnav-hamburger"
        onClick={() => setDrawerOpen((o) => !o)}
        aria-label="Menu"
        aria-expanded={drawerOpen}
      >
        <Icon name={drawerOpen ? 'close' : 'menu'} size={20} />
      </button>
      <div className="topnav-brand" onClick={() => navigate('dashboard')} style={{ cursor: 'pointer' }}>
        <div className="topnav-brand-mark" style={{ display: 'grid', placeItems: 'center' }}>
          <BrandLogo size={26} />
        </div>
        <span className="topnav-prod">{orgName}</span>
      </div>
      <div className="topnav-spacer" />
      <div className="topnav-actions">
        <ThemeToggle className="topnav-btn" />
        <button
          className="topnav-btn lang-toggle"
          onClick={() => setLang(lang === 'id' ? 'en' : 'id')}
          title="Switch language"
          style={{ width: 'auto', padding: '0 8px', fontSize: 11, fontFamily: 'var(--font-mono)' }}
        >
          <span className={lang === 'id' ? 'on' : 'off'}>ID</span>
          <span style={{ color: 'var(--text-4)', fontWeight: 400 }}>/</span>
          <span className={lang === 'en' ? 'on' : 'off'}>EN</span>
        </button>
        <button
          className="topnav-btn"
          title={t.notifications}
          onClick={onBellClick}
          style={{ position: 'relative' }}
        >
          <Icon name="bell" size={16} />
          {notifCount > 0 && (
            <span className="notif-badge">{notifCount > 99 ? '99+' : notifCount}</span>
          )}
        </button>
        {/* Sepadan dengan tombol bantuan di tampilan lebar. Tanpa ini, pengguna
            tablet dan ponsel tidak punya jalan sama sekali untuk membuka
            kembali layar sambutan. */}
        <button
          className="topnav-btn"
          title={lang === 'id' ? 'Panduan & tindakan cepat' : 'Guide & quick actions'}
          aria-label={lang === 'id' ? 'Panduan & tindakan cepat' : 'Guide & quick actions'}
          onClick={() => navigate('welcome')}
        >
          <Icon name="help" size={16} />
        </button>
        <div className="topnav-btn" style={{ cursor: 'default' }}>
          <div className="avatar">{initials}</div>
        </div>
        <button className="topnav-btn" onClick={logout} title={t.logout}>
          <Icon name="logout" size={16} />
        </button>
      </div>
    </header>
  )
}

// --- Placeholder screens ---
function VideoLibraryScreen() {
  const t = useT()
  const { lang, role, navigate, currentProgramId } = useApp()
  const orgName = useOrgName()
  const canUpload = role === "trainer" || role === "admin"
  const [videos, setVideos] = React.useState([])
  const [loading, setLoading] = React.useState(false)
  const [error, setError] = React.useState(null)
  const [showUpload, setShowUpload] = React.useState(false)

  const reload = React.useCallback(() => {
    if (!currentProgramId) { setVideos([]); return }
    setLoading(true)
    setError(null)
    listProgramVideos(currentProgramId)
      .then(setVideos)
      .catch((e) => setError(e?.response?.data?.error || e.message))
      .finally(() => setLoading(false))
  }, [currentProgramId])

  React.useEffect(() => { reload() }, [reload])

  const handleDelete = async (videoId) => {
    if (!window.confirm(lang === "id" ? "Hapus video ini?" : "Delete this video?")) return
    try {
      await deleteVideo(currentProgramId, videoId)
      reload()
    } catch (e) {
      alert(e?.response?.data?.error || e.message)
    }
  }

  const fmtDuration = (sec) => {
    if (!sec) return null
    const m = Math.floor(sec / 60), s = sec % 60
    return `${m}:${String(s).padStart(2, "0")}`
  }

  return (
    <div className="main-inner">
      <div className="page-header">
        <div className="page-header-text">
          <div className="eyebrow">{orgName} · {lang === "id" ? "Media" : "Media"}</div>
          <h1 className="h1" style={{ marginTop: 6 }}>{t.videos}</h1>
          <div className="muted mt-1">{lang === "id" ? "Daftar video pelatihan untuk semua kursus." : "VOD video library for all courses."}</div>
        </div>
        {canUpload && (
          <div className="page-header-actions">
            <button className="btn" onClick={() => setShowUpload(true)} disabled={!currentProgramId}>
              <Icon name="upload" size={14}/>
              {lang === "id" ? "Upload Video" : "Upload Video"}
            </button>
          </div>
        )}
      </div>

      {error && <div className="banner red mt-4"><Icon name="alert"/>{error}</div>}

      {loading ? (
        <div className="muted mt-5">{lang === "id" ? "Memuat..." : "Loading..."}</div>
      ) : videos.length === 0 ? (
        <div className="tile mt-5 tile-pad" style={{display:"grid", placeItems:"center", height: 240, color: "var(--text-tertiary)"}}>
          {lang === "id" ? "Belum ada video. " : "No videos yet. "}
          {canUpload && (lang === "id" ? "Klik “Upload Video” untuk menambahkan." : "Click “Upload Video” to add one.")}
        </div>
      ) : (
        <div className="grid grid-3 mt-5">
          {videos.map((v) => (
            <div key={v.id} className="tile" style={{cursor:"pointer"}}>
              <div className="ph-img" style={{aspectRatio:"16/9"}} onClick={() => navigate("video")}>
                {v.thumbnail_url ? <img src={v.thumbnail_url} alt="" style={{width:"100%",height:"100%",objectFit:"cover"}}/> : "[ video ]"}
              </div>
              <div className="tile-body">
                <div className="h4">{v.title}</div>
                <div className="muted small mt-1">
                  {v.lesson?.module?.course?.title} · {v.lesson?.module?.title}
                  {fmtDuration(v.duration_sec) ? ` · ${fmtDuration(v.duration_sec)}` : ""}
                </div>
                {canUpload && (
                  <div className="row gap-2 mt-2">
                    <Btn variant="ghost" size="sm" icon={<Icon name="trash" size={12}/>} onClick={(e) => { e.stopPropagation(); handleDelete(v.id) }}>
                      {lang === "id" ? "Hapus" : "Delete"}
                    </Btn>
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      {showUpload && (
        <UploadVideoModal
          programId={currentProgramId}
          onClose={() => setShowUpload(false)}
          onUploaded={() => { setShowUpload(false); reload() }}
        />
      )}
    </div>
  )
}

function UploadVideoModal({ programId, onClose, onUploaded }) {
  const { lang } = useApp()
  const [courses, setCourses] = React.useState([])
  const [courseId, setCourseId] = React.useState("")
  const [courseDetail, setCourseDetail] = React.useState(null)
  const [lessonId, setLessonId] = React.useState("")
  const [title, setTitle] = React.useState("")
  const [file, setFile] = React.useState(null)
  const [busy, setBusy] = React.useState(false)
  const [err, setErr] = React.useState(null)

  React.useEffect(() => {
    listCourses(programId).then(setCourses).catch((e) => setErr(e.message))
  }, [programId])

  React.useEffect(() => {
    if (!courseId) { setCourseDetail(null); setLessonId(""); return }
    getCourse(programId, courseId).then(setCourseDetail).catch((e) => setErr(e.message))
  }, [programId, courseId])

  const lessons = React.useMemo(() => {
    if (!courseDetail?.modules) return []
    return courseDetail.modules.flatMap((m) =>
      m.lessons.map((l) => ({ id: l.id, label: `${m.title} · ${l.title}`, hasVideo: !!l.video }))
    )
  }, [courseDetail])

  const submit = async (e) => {
    e.preventDefault()
    if (!file || !lessonId) return
    setBusy(true); setErr(null)
    try {
      await uploadVideoForLesson(programId, lessonId, file, { title: title || undefined })
      onUploaded()
    } catch (ex) {
      setErr(ex?.response?.data?.error || ex.message)
    } finally {
      setBusy(false)
    }
  }

  return (
    <div className="modal-overlay" style={{position:"fixed", inset:0, background:"rgba(0,0,0,.5)", display:"grid", placeItems:"center", zIndex:50}} onClick={onClose}>
      <form onClick={(e) => e.stopPropagation()} onSubmit={submit} className="tile" style={{width: 480, background:"var(--surface)"}}>
        <div className="tile-header">
          <h3>{lang === "id" ? "Upload Video" : "Upload Video"}</h3>
          <Btn variant="ghost" size="sm" icon={<Icon name="close" size={14}/>} onClick={onClose}/>
        </div>
        <div className="col gap-3" style={{padding:"16px"}}>
          {err && <div className="banner red"><Icon name="alert"/>{err}</div>}

          <label className="col gap-1">
            <span className="small">{lang === "id" ? "Kursus" : "Course"}</span>
            <select value={courseId} onChange={(e) => setCourseId(e.target.value)} required
              style={{height:32, padding:"0 8px", border:"1px solid var(--border-strong)"}}>
              <option value="">{lang === "id" ? "Pilih kursus..." : "Select course..."}</option>
              {courses.map((c) => <option key={c.id} value={c.id}>{c.title}</option>)}
            </select>
          </label>

          <label className="col gap-1">
            <span className="small">{lang === "id" ? "Pelajaran" : "Lesson"}</span>
            <select value={lessonId} onChange={(e) => setLessonId(e.target.value)} required disabled={!courseId}
              style={{height:32, padding:"0 8px", border:"1px solid var(--border-strong)"}}>
              <option value="">{lang === "id" ? "Pilih pelajaran..." : "Select lesson..."}</option>
              {lessons.map((l) => (
                <option key={l.id} value={l.id}>
                  {l.label}{l.hasVideo ? (lang === "id" ? " (akan diganti)" : " (will replace)") : ""}
                </option>
              ))}
            </select>
          </label>

          <label className="col gap-1">
            <span className="small">{lang === "id" ? "Judul (opsional)" : "Title (optional)"}</span>
            <input value={title} onChange={(e) => setTitle(e.target.value)}
              placeholder={lang === "id" ? "Default: nama file" : "Default: file name"}
              style={{height:32, padding:"0 8px", border:"1px solid var(--border-strong)"}}/>
          </label>

          <label className="col gap-1">
            <span className="small">{lang === "id" ? "Berkas video" : "Video file"}</span>
            <input type="file" accept="video/*" required
              onChange={(e) => setFile(e.target.files?.[0] || null)}/>
            <span className="tiny muted">{lang === "id" ? "Maks 500 MB · MP4/MOV/WEBM" : "Max 500 MB · MP4/MOV/WEBM"}</span>
          </label>
        </div>
        <div className="tile-foot">
          <Btn variant="ghost" size="sm" onClick={onClose} disabled={busy}>{lang === "id" ? "Batal" : "Cancel"}</Btn>
          <Btn variant="primary" size="sm" type="submit" disabled={busy || !file || !lessonId}>
            {busy ? (lang === "id" ? "Sedang upload..." : "Uploading...") : (lang === "id" ? "Upload" : "Upload")}
          </Btn>
        </div>
      </form>
    </div>
  )
}

// Halaman yang boleh dituju lewat ?goto= dari sebuah QR. Sengaja daftar tetap:
// nilai ini datang dari URL yang bisa disusun siapa saja, jadi meneruskannya
// mentah-mentah ke navigate() berarti orang lain bisa mengarahkan peserta ke
// halaman mana pun di aplikasi.
const TUJUAN_GOTO = ['pretest', 'posttest', 'feedback']

// --- Main App ---
export default function App() {
  const { page, role, lang, isAuthenticated, navigate, drawerOpen, setDrawerOpen, currentProgramId, sidebarCollapsed } = useApp()

  // Detect query parameters to auto-route to public pages
  React.useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const verifyCert = params.get('verify');
    const collectionSlug = params.get('collection');
    if (verifyCert) {
      navigate('verify-public', { certNo: verifyCert });
    } else if (collectionSlug) {
      navigate('collection-public', { slug: collectionSlug });
    } else if (params.get('review')) {
      // Tautan penilaian kinerja yang dikirim ke atasan lewat email. Ia
      // tidak punya akun dan tidak perlu punya — tokennya sendiri kuncinya.
      navigate('appraisal-review', { token: params.get('review') });
    } else if (params.has('claim')) {
      // Alamat yang dikirim ke SETIAP penerima sertifikat lewat email —
      // `claim_url` di email.service.js adalah `<lembaga>/?claim=1`.
      //
      // Sebelumnya parameter ini tidak ditangani sama sekali, jadi penerimanya
      // jatuh ke efek "paksa ke login" di bawah. Mereka justru orang yang
      // paling pasti TIDAK punya akun: seluruh alur klaim ada supaya sertifikat
      // bisa diambil tanpa mendaftar. Yang mereka lihat adalah layar masuk yang
      // menolak mereka, tanpa satu pun petunjuk bahwa halaman klaimnya ada.
      navigate('claim');
    }
  }, [navigate]);

  // ── FR-19 notification state ──────────────────────────────────
  const [notifOpen, setNotifOpen]   = React.useState(false)
  const [notifCount, setNotifCount] = React.useState(0)
  const [aiPanelOpen, setAiPanelOpen] = React.useState(false)

  // ── FR-30 Welcome onboarding gate ─────────────────────────────
  // The first time a participant lands in a program whose enrollment has not
  // been onboarded (onboarded_at = null), route them to the Welcome screen.
  // Checked once per program per session; dismissing the screen PATCHes
  // onboarded_at so it won't recur.
  const onboardCheckedRef = React.useRef(new Set())
  // Dibaca di dalam .then() supaya SELALU nilai TERBARU saat permintaan
  // kembali, bukan closure basi dari saat efek ini pertama jalan — peserta
  // bisa saja sudah pindah layar (mengisi feedback, mengerjakan pretest, dst.)
  // sebelum getMyEnrollment() selesai.
  const pageSaatIniRef = React.useRef(page)
  pageSaatIniRef.current = page
  React.useEffect(() => {
    if (!isAuthenticated || role !== 'participant' || !currentProgramId) return
    if (onboardCheckedRef.current.has(currentProgramId)) return
    onboardCheckedRef.current.add(currentProgramId)
    getMyEnrollment(currentProgramId)
      .then((enr) => {
        // Gerbang ini cuma berhak jalan selagi peserta masih di dashboard —
        // begitu dia sudah berpindah ke layar lain (form apa pun), menimpanya
        // dengan Welcome akan menutup form itu tanpa peringatan. Dulu efek ini
        // langsung navigate('welcome') tanpa mengecek apa yang sedang dibuka,
        // dan itu sebabnya form peserta bisa "tiba-tiba tertutup sendiri".
        if (enr && !enr.onboarded_at && pageSaatIniRef.current === 'dashboard') {
          navigate('welcome')
        }
      })
      .catch(() => {})
  }, [isAuthenticated, role, currentProgramId, navigate])

  // ── ?goto=pretest|posttest ────────────────────────────────────
  // Peserta memindai QR yang diproyeksikan instruktur, login seperti biasa,
  // lalu mendarat langsung di layar tes.
  //
  // Tidak bisa ikut menumpang efek ?verify= di atas: efek itu jalan sekali saat
  // mount dan dep-nya `navigate` yang stabil selamanya, jadi ia tidak pernah
  // dapat kesempatan kedua setelah login — sementara pretest/posttest justru
  // baru boleh dibuka SETELAH login. Halaman non-publik juga akan dipental
  // balik ke login oleh efek di bawah selama belum terautentikasi.
  //
  // URL-nya sendiri yang jadi tempat penitipan tujuan: tidak ada kode yang
  // membersihkan window.location.search, jadi parameternya bertahan melewati
  // transisi login tanpa perlu penyimpanan tujuan-tertunda yang baru.
  const gotoDipakaiRef = React.useRef(false)
  React.useEffect(() => {
    if (gotoDipakaiRef.current) return
    if (!isAuthenticated || !currentProgramId) return
    // Layar sambutan berhak mendahului; tesnya menyusul setelah itu.
    if (page === 'welcome') return

    const tujuan = new URLSearchParams(window.location.search).get('goto')
    // Daftar-putih, bukan sekadar diteruskan: jangan pernah navigate() ke
    // halaman sembarang yang datang dari URL.
    //
    // Pasangannya ada di TUJUAN_QR pada routes/programs.js, yang memutuskan QR
    // apa saja yang boleh dibuat. Menambah tujuan di sana tanpa menambahkannya
    // di sini menghasilkan QR yang dipindai lalu tidak melakukan apa-apa.
    if (!TUJUAN_GOTO.includes(tujuan)) return

    gotoDipakaiRef.current = true
    navigate(tujuan)

    // Dibuang supaya muat ulang berikutnya tidak menyeret peserta kembali ke
    // halaman tes. Idiom yang sama dipakai alur ?reset= di LoginScreen.
    const url = new URL(window.location.href)
    url.searchParams.delete('goto')
    window.history.replaceState({}, '', url.toString())
  }, [isAuthenticated, currentProgramId, page, navigate])

  // Poll unread count every 60 s while authenticated; also fetch on login
  React.useEffect(() => {
    if (!isAuthenticated) { setNotifCount(0); return }
    let cancelled = false
    const fetch = () =>
      unreadNotifCount()
        .then((n) => { if (!cancelled) setNotifCount(n) })
        .catch(() => {})
    fetch()
    const id = setInterval(fetch, 60000)
    return () => { cancelled = true; clearInterval(id) }
  }, [isAuthenticated])

  const handleBellClick = () => setNotifOpen((o) => !o)
  // ─────────────────────────────────────────────────────────────

  // Force unauthenticated users to login/claim/verify screens
  React.useEffect(() => {
    if (!isAuthenticated && page !== 'login' && page !== 'mfa-setup' && page !== 'claim' && page !== 'verify-public' && page !== 'collection-public' && page !== 'appraisal-review') {
      navigate('login')
    }
  }, [isAuthenticated, page, navigate])

  const renderPage = () => {
    if (page === "login") return <LoginScreen />
    if (page === "mfa-setup") return <MfaSetupScreen />
    if (page === "claim") return <ClaimScreen />
    if (page === "verify-public") return <VerifyPublicScreen />
    if (page === "collection-public") return <PublicCollectionScreen />
    if (page === "appraisal-review") return <AppraisalReviewScreen />
    if (page === "penilaian") return <AppraisalScreen />
    if (page === "program-selector") return <ProgramSelectorScreen />
    if (page === "welcome") {
      if (role === "participant") return <ParticipantWelcomeScreen />
      return <AdminWelcomeScreen />
    }
    if (page === "dashboard") {
      if (role === "participant") return <ParticipantDashboard />
      if (role === "trainer") return <TrainerDashboard />
      if (role === "admin") return <AdminDashboard />
    }
    if (page === "courses" || page === "course") return <CourseDetailScreen />
    if (page === "course-manage") return <CourseManageScreen />
    if (page === "programs-manage") return <ProgramManageScreen />
    if (page === "video") return <VideoPlayerScreen />
    if (page === "game") return <LessonGameScreen />
    if (page === "sessions" || page === "training" || page === "training-hub") return <TrainingScreen />
    if (page === "session-detail") return <SessionDetailScreen />
    if (page === "emails") return <EmailsScreen />
    if (page === "qr") return <QRAttendanceScreen />
    if (page === "ai") return <AIChatScreen />
    if (page === "kb") return <KnowledgeBaseScreen />
    if (page === "doc-library") return <CMSScreen />
    if (page === "platform") return <PlatformScreen />
    if (page === "feedback") return <FeedbackScreen />
    if (page === "pretest") return <TestScreen />
    if (page === "posttest") return <TestScreen />
    if (page === "learning-gain") return <LearningGainScreen />
    if (page === "cert-send") return <AdminCertSendScreen />
    if (page === "certificates") return <CertificateScreen />
    if (page === "progress") return <ProgressAnalyticsScreen />
    if (page === "users") return <UsersScreen />
    if (page === "reports") return <ReportsScreen />
    if (page === "videos") return <VideoLibraryScreen />
    if (page === "test-editor") return <TestEditorScreen />
    if (page === "program-detail") return <ProgramDetailScreen />
    if (page === "settings") return <SettingsScreen />
    if (page === "zoom") return <ZoomScreen />
    if (page === "enrollment") return <EnrollmentScreen />
    if (page === "classes") return <TrainingScreen />
    return <ParticipantDashboard />
  }

  const noShell = page === "login" || page === "mfa-setup" || page === "claim" || page === "program-selector" || page === "welcome" || page === "verify-public" || page === "collection-public" || page === "appraisal-review"

  return (
    <div className={"app" + (noShell ? " no-shell" : "") + (drawerOpen ? " drawer-open" : "") + (sidebarCollapsed ? " sidebar-collapsed" : "")}>
      {!noShell && <Sidebar />}
      {/* Mobile header — CSS hides it on desktop, shows on ≤768px */}
      {!noShell && <TopNav notifCount={notifCount} onBellClick={handleBellClick} />}
      {!noShell && drawerOpen && (
        <div className="drawer-backdrop" onClick={() => setDrawerOpen(false)} />
      )}
      <div className="main-area">
        {/* Desktop topbar — CSS hides it on mobile */}
        {!noShell && <Topbar notifCount={notifCount} onBellClick={handleBellClick} />}
        <main className="main">
          {renderPage()}
        </main>
      </div>
      {/* Notification panel — rendered above everything, outside layout grid */}
      {!noShell && notifOpen && (
        <NotificationsPanel
          onClose={() => setNotifOpen(false)}
          onCountChange={setNotifCount}
        />
      )}
      {/* AI Floating Panel — participants only */}
      {!noShell && role === "participant" && (
        <AIFloatingPanel open={aiPanelOpen} onToggle={() => setAiPanelOpen((o) => !o)} />
      )}
      {/* Dashboard Tutorial — participants only, first login */}
      {!noShell && role === "participant" && page === "dashboard" && (
        <DashboardTutorial lang={lang} />
      )}
    </div>
  )
}
